
import React, {
  useEffect,
  useState,
  useCallback,
  useContext,
  Fragment,
  useRef,
  useMemo,
} from "react";
import AppContext from "../../../Store/AppContext";
import WhatsAppChat from "./../WhatsAppChat";
import {
  ListarConversasEmAndamento,
  ObterTotalMsgNaoLidas,
  ObterPermissoesChatWhatsApp
} from "../../../Services/WhatsApp";
import WhatsAppConversationActions from "../WhatsAppConversationActions/WhatsAppConversationActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import "./ListaConversasWhatsApp.css";
import ChatNovoChatSender from "./../ChatComponente/ChatNovoChatSender";
import ChatEtiquetaManager from "./../ChatComponente/ChatEtiquetaManager";
import OperadorContextManager from "./../ChatComponente/OperadorContextManager";
import UserClockIcon from "./../ChatComponente/UserClockIcon";
import MessageClockIcon from "./../ChatComponente/MessageClockIcon";


const INTERVALO_ATUALIZACAO_MS = 5000;

const QUICK = {
  NAO_LIDAS: "NAO_LIDAS",
  NAO_RESPONDIDAS: "NAO_RESPONDIDAS",
  MINHAS: "MINHAS",
  SEM_INTERACAO: "SEM_INTERACAO",
};



const ListaConversasWhatsApp = ({ id }) => {
  const { contaWhatsAppDefault, session } = useContext(AppContext);

  //PERMISSOES CHAT
  const [permissoesChat, setPermissoesChat] = useState(null);
  const [permissoesChatLoading, setPermissoesChatLoading] = useState(false);
  const permissoesReqIdRef = useRef(0);
  const permissoesEmAndamentoRef = useRef(false);

  //CONTEXTO DE OPERADOR 
  const [modalOperadorAberto, setModalOperadorAberto] = useState(false);
  const [operadorContexto, setOperadorContexto] = useState({
    usuarioId: session.usuarioId,
    usuarioNome: session.usuarioNome,
    usuarioLogin: session.usuarioLogin,
  });
  const operadorContextoId = operadorContexto?.usuarioId || session.usuarioId;
  const nomeOperadorExibido =
    operadorContexto?.usuarioNome ||
    operadorContexto?.usuarioLogin ||
    session?.usuarioNome ||
    `Usuário ${operadorContextoId}`;


  const [conversas, setConversas] = useState([]);
  const [conversaSelecionada, setConversaSelecionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const [modalAberto, setModalAberto] = useState(false);
  const [actionAtivada, setActionAtivada] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [quickFilters, setQuickFilters] = useState(new Set());

  const [novoChatAberto, setNovoChatAberto] = useState(false);
  const [modalFiltroEtiquetasAberto, setModalFiltroEtiquetasAberto] = useState(false);
  const [filtroEtiquetaIds, setFiltroEtiquetaIds] = useState([]);

  const modalId = `modalModalListaConversasWhatsApp${id}`;
  const modalRef = useRef(null);

  // Guards contra concorrência / resposta atrasada
  const conversasEmAndamentoRef = useRef(false);
  const conversasReqIdRef = useRef(0);

  const totalEmAndamentoRef = useRef(false);
  const totalReqIdRef = useRef(0);

  const carregarPermissoesChat = useCallback(async () => {
    if (permissoesEmAndamentoRef.current) return;

    permissoesEmAndamentoRef.current = true;
    const reqId = ++permissoesReqIdRef.current;

    try {
      setPermissoesChatLoading(true);

      const { success, permissoes } = await ObterPermissoesChatWhatsApp();

      if (reqId !== permissoesReqIdRef.current) return;

      if (success) {
        setPermissoesChat(permissoes || null);
      } else {
        setPermissoesChat(null);
      }
    } catch (e) {
      if (reqId !== permissoesReqIdRef.current) return;
      setPermissoesChat(null);
    } finally {
      if (reqId === permissoesReqIdRef.current) setPermissoesChatLoading(false);
      permissoesEmAndamentoRef.current = false;
    }
  }, []);

  useEffect(() => {
    carregarPermissoesChat();

    return () => {
      // invalida respostas atrasadas ao desmontar
      permissoesReqIdRef.current++;
    };
  }, [carregarPermissoesChat]);

  const podeTrocarOperador = useMemo(() => {

    const bySession = session?.permissoes?.podeTrocarOperador;
    if (typeof bySession === "boolean") return bySession;

    const byApi = permissoesChat?.podeMudarOperador ?? permissoesChat?.podeTrocarOperador;
    return !!byApi;
  }, [session?.permissoes?.podeTrocarOperador, permissoesChat]);

  useEffect(() => {
    setOperadorContexto({
      usuarioId: session.usuarioId,
      usuarioNome: session.usuarioNome,
      usuarioLogin: session.usuarioLogin,
    });
  }, [session.usuarioId, session.usuarioNome, session.usuarioLogin]);

  const normalizarTexto = (txt) =>
    (txt || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const conversasBaseFiltradas = useMemo(() => {
    const f = normalizarTexto(filtro);
    const filtroEtiquetasAtivo = Array.isArray(filtroEtiquetaIds) && filtroEtiquetaIds.length > 0;
    const filtroSet = new Set((filtroEtiquetaIds || []).map(Number).filter((x) => x > 0));

    const parseEtiquetas = (csv) =>
      !csv ? [] : String(csv).split(",").map((x) => Number(String(x).trim())).filter((x) => x > 0);

    return (conversas || []).filter((c) => {
      // 1) texto
      const passaTexto = !f
        ? true
        : (() => {
          const nome = normalizarTexto(c.nome);
          const tel = normalizarTexto(`${c.ddi || ""}${c.ddd || ""}${c.telefone || ""}`);
          return nome.includes(f) || tel.includes(f);
        })();

      if (!passaTexto) return false;

      // 2) etiquetas
      if (filtroEtiquetasAtivo) {
        const etiquetasDaConversa = parseEtiquetas(c.etiquetasConversa);
        const passaEtiqueta = etiquetasDaConversa.some((id) => filtroSet.has(id));
        if (!passaEtiqueta) return false;
      }

      return true;
    });
  }, [conversas, filtro, filtroEtiquetaIds]);

  const conversasFiltradas = useMemo(() => {
    return (conversasBaseFiltradas || []).filter((c) => {
      if (quickFilters.has(QUICK.NAO_LIDAS)) {
        if ((c.totalNaoLidas || 0) <= 0) return false;
      }

      if (quickFilters.has(QUICK.NAO_RESPONDIDAS)) {
        const st = String(c.ultimoStatus || "").toLowerCase().trim();
        if (st !== "recebida") return false;
      }

      if (quickFilters.has(QUICK.MINHAS)) {
        if (Number(c.ultimoOperadorUsuarioId || 0) !== Number(operadorContextoId)) return false;
      }

      if (quickFilters.has(QUICK.SEM_INTERACAO)) {
        if (Number(c.ultimoOperadorUsuarioId || 0) !== 0) return false;
      }

      return true;
    });
  }, [conversasBaseFiltradas, quickFilters, operadorContextoId]);


  const totalConversasNaoLidas = useMemo(() => {
    return (conversasFiltradas || []).filter(c => (c.totalNaoLidas || 0) > 0).length;
  }, [conversasFiltradas]);

  const totalConversasSemInteracao = useMemo(() => {
    return (conversasBaseFiltradas || []).filter(c => Number(c.ultimoOperadorUsuarioId || 0) === 0).length;
  }, [conversasBaseFiltradas]);


  const carregarConversas = useCallback(async () => {
    if (!modalAberto) return;

    conversasEmAndamentoRef.current = true;
    const reqId = ++conversasReqIdRef.current;

    try {
      setErro(null);
      if (conversas.length === 0) setLoading(true);

      const { success, conversas: conversasApi } =
        await ListarConversasEmAndamento(
          contaWhatsAppDefault,
          operadorContextoId
        );

      if (reqId !== conversasReqIdRef.current) return;

      if (success) setConversas(conversasApi || []);
      else setErro("Não foi possível carregar as conversas.");
    } catch (e) {
      console.error("Erro ao listar conversas WhatsApp:", e);
      if (reqId !== conversasReqIdRef.current) return;
      setErro("Não foi possível carregar as conversas.");
    } finally {
      if (reqId === conversasReqIdRef.current) setLoading(false);
      conversasEmAndamentoRef.current = false;
    }
  }, [
    modalAberto,
    contaWhatsAppDefault,
    operadorContextoId,
    conversas.length
  ]);

  // Sync conversaSelecionada com a lista quando conversas atualizam
  useEffect(() => {
    if (!conversaSelecionada) return;

    const match = (c) =>
      (c.ddi || "55") === (conversaSelecionada.ddi || "55") &&
      c.ddd === conversaSelecionada.ddd &&
      c.telefone === conversaSelecionada.telefone;

    const encontrada = (conversas || []).find(match);

    // Se for MANUAL e agora existe no banco => substitui pelo objeto real
    if (conversaSelecionada.__manual) {
      if (encontrada) {
        setConversaSelecionada({ ...encontrada });
      }
      return;
    }

    // Se não for manual:
    // - se sumiu da lista, limpa
    if (!encontrada) {
      setConversaSelecionada(null);
      return;
    }

    // - se existe e veio atualizada, sincroniza
    if (encontrada !== conversaSelecionada) {
      setConversaSelecionada(encontrada);
    }
  }, [conversas, conversaSelecionada]);

  // Polling do total não lidas
  useEffect(() => {
    let cancelado = false;

    const carregarTotalNaoLidas = async () => {
      if (cancelado) return;

      if (totalEmAndamentoRef.current) return;
      totalEmAndamentoRef.current = true;

      const reqId = ++totalReqIdRef.current;

      try {
        const { success, totalNaoLidas } =
          await ObterTotalMsgNaoLidas(contaWhatsAppDefault, operadorContextoId);

        if (cancelado) return;
        if (reqId !== totalReqIdRef.current) return;

        if (success) setTotalNaoLidas(totalNaoLidas);
      } catch (err) {
        console.error("Erro ao obter total de mensagens não lidas:", err);
      } finally {
        totalEmAndamentoRef.current = false;
      }
    };

    carregarTotalNaoLidas();
    const intervalId = setInterval(
      carregarTotalNaoLidas,
      INTERVALO_ATUALIZACAO_MS,
    );

    return () => {
      cancelado = true;
      clearInterval(intervalId);
    };
  }, [contaWhatsAppDefault, operadorContextoId]);

  // Polling conversas só com modal aberto


  // Polling conversas só com modal aberto
  useEffect(() => {
    if (!modalAberto) return;

    let cancelado = false;

    const carregar = async () => {
      if (cancelado) return;
      await carregarConversas();
    };

    carregar();
    const intervalId = setInterval(carregar, INTERVALO_ATUALIZACAO_MS);

    return () => {
      cancelado = true;
      clearInterval(intervalId);
    };
  }, [modalAberto, carregarConversas]);

  useEffect(() => {
    const el = document.getElementById(modalId);
    if (!el) return;

    modalRef.current = el;

    const onHidden = () => {
      setModalAberto(false);
      setConversaSelecionada(null);

      conversasReqIdRef.current++;
      totalReqIdRef.current++;

      // se fechar o modal principal, garante fechar o "novo chat" também
      setNovoChatAberto(false);
    };

    el.addEventListener("hidden.bs.modal", onHidden);

    return () => {
      el.removeEventListener("hidden.bs.modal", onHidden);
    };
  }, [modalId]);

  // Não matar conversa manual
  useEffect(() => {
    if (!conversaSelecionada) return;
    if (conversaSelecionada.__manual) return;

    const aindaExiste = (conversasFiltradas || []).some(
      (c) =>
        c.ddi === conversaSelecionada.ddi &&
        c.ddd === conversaSelecionada.ddd &&
        c.telefone === conversaSelecionada.telefone,
    );

    if (!aindaExiste) setConversaSelecionada(null);
  }, [conversasFiltradas, conversaSelecionada]);

  const formatarDataHora = (dataHoraIso) => {
    if (!dataHoraIso) return "";
    const data = new Date(dataHoraIso);

    const agora = new Date();
    const mesmoDia =
      data.getDate() === agora.getDate() &&
      data.getMonth() === agora.getMonth() &&
      data.getFullYear() === agora.getFullYear();

    if (mesmoDia) {
      return data.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return data.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const obterIniciais = (nome) => {
    if (!nome || typeof nome !== "string") return "?";
    const partes = nome.trim().split(/\s+/);
    if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
    return (
      partes[0].charAt(0).toUpperCase() +
      partes[partes.length - 1].charAt(0).toUpperCase()
    );
  };


  const renderConversaItem = (conversa) => {
    const {
      ddi,
      ddd,
      telefone,
      nome,
      textoUltimaMensagem,
      dataHoraUltimaMensagem,
      totalNaoLidas,
      ultimoOperadorUsuarioId,
      ultimoStatus,
    } = conversa;

    const semInteracao = Number(ultimoOperadorUsuarioId || 0) === 0;

    const naoRespondida =
      String(ultimoStatus || "").toLowerCase().trim() === "recebida";

    const selecionada =
      conversaSelecionada &&
      conversaSelecionada.ddi === ddi &&
      conversaSelecionada.ddd === ddd &&
      conversaSelecionada.telefone === telefone;

    const keyItem = `${ddi}${ddd}${telefone}`;

    const deveExibirIndicadores =
      semInteracao || (!semInteracao && naoRespondida) || (Number(totalNaoLidas || 0) > 0);

    return (
      <button
        key={keyItem}
        type="button"
        className={`lista-conversas-item ${selecionada ? "selecionada" : ""}`}
        onClick={() => setConversaSelecionada(conversa)}
      >
        <div className="lista-conversas-avatar">
          <span>{obterIniciais(nome)}</span>
        </div>

        <div className="lista-conversas-conteudo">
          <div className="lista-conversas-header">
            <span className="lista-conversas-nome">
              <span className="lista-conversas-nome-texto" title={nome}>
                {nome}
              </span>
            </span>

            <span className="lista-conversas-hora">
              {formatarDataHora(dataHoraUltimaMensagem)}
            </span>
          </div>

          <div className="lista-conversas-footer">
            <span className="lista-conversas-ultima-msg">
              {textoUltimaMensagem || "Sem mensagens ainda"}
            </span>

            {deveExibirIndicadores && (
              <span className="nao-lidas-wrapper">
                {semInteracao && (
                  <UserClockIcon
                    size={14}
                    className="icone-sem-interacao"
                    title="Aguardando primeiro atendimento"
                  />
                )}

                {!semInteracao && naoRespondida && (
                  <MessageClockIcon
                    size={14}
                    className="icone-nao-respondida"
                    title="Mensagem aguardando resposta"
                  />
                )}

                {Number(totalNaoLidas || 0) > 0 && (
                  <span className="lista-conversas-nao-lidas">
                    {totalNaoLidas}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };


  const actionAtivaCallback = (actionAtiva) => setActionAtivada(actionAtiva);

  const iniciarConversaManual = ({ ddi, ddd, telefone }) => {
    setConversaSelecionada({
      ddi: ddi || "55",
      ddd,
      telefone,
      nome: `${ddd}${telefone}`,
      textoUltimaMensagem: "",
      dataHoraUltimaMensagem: null,
      totalNaoLidas: 0,
      __manual: true,
    });
    setNovoChatAberto(false);
  };

  const abrirNovoChat = () => {
    setNovoChatAberto(true);
  };

  const fecharNovoChat = () => {
    setNovoChatAberto(false);
  };

  const handleFiltroChange = (e) => {
    setFiltro(e.target.value);
  };


  const toggleQuick = (key) => {
    setQuickFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const limparQuick = () => setQuickFilters(new Set());


  return (
    <Fragment>
      {/* Botão WhatsApp */}
      <div
        className="componenteListaConversasWhatsApp_container"
        role="button"
        data-toggle="modal"
        data-target={`#${modalId}`}
        onClick={() => {
          setModalAberto(true);
          setConversaSelecionada(null);
        }}
      >
        <div className="whatsapp-icon-wrapper">
          <FontAwesomeIcon className="whatsapp-icon" icon={faWhatsapp} />
          {totalNaoLidas > 0 && (
            <span className="whatsapp-notification-badge">{totalNaoLidas}</span>
          )}
        </div>
      </div>



      {/* Modal principal */}
      <div
        className="modal fade"
        id={modalId}
        tabIndex="-1"
        role="dialog"
        aria-labelledby={`${modalId}Label`}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
          <div className="modal-content componenteListaConversasWhatsApp_modalContent">
            <div className="modal-header componenteListaConversasWhatsApp_headerWhatApp">
              <h5 className="modal-title" id={`${modalId}Label`}>
                Conversas WhatsApp
              </h5>

              <div className="lista-conversas-header-right">
                {/* Operador no topo, ao lado do fechar */}
                <div className="lista-conversas-operador-topo">
                  <span className="lista-conversas-operador-label">Operador:</span>

                  {podeTrocarOperador ? (
                    <button
                      type="button"
                      className="lista-conversas-operador-chip"
                      onClick={() => setModalOperadorAberto(true)}
                      title="Alterar operador do contexto"
                    >
                      <span className="lista-conversas-operador-nome">
                        {nomeOperadorExibido || `Usuário ${operadorContextoId}`}
                      </span>
                      <span className="lista-conversas-operador-caret">▾</span>
                    </button>
                  ) : (
                    <span className="lista-conversas-operador-chip lista-conversas-operador-chip--static">
                      <span className="lista-conversas-operador-nome">
                        {nomeOperadorExibido || `Usuário ${operadorContextoId}`}
                      </span>
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    setModalAberto(false);
                    setConversaSelecionada(null);
                    setNovoChatAberto(false);
                  }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            </div>


            <div className="modal-body componenteListaConversasWhatsApp_modalBody">
              <div className="row" style={{ display: "flow" }}>
                <div className="lista-conversas-container">
                  <div className="lista-conversas-sidebar">
                    <div className="lista-conversas-sidebar-header">
                      <div className="lista-conversas-header-actions">
                        <input
                          type="text"
                          className="lista-conversas-pesquisa"
                          placeholder="Pesquisar contato..."
                          value={filtro}
                          onChange={handleFiltroChange}
                          autoComplete="off"
                        />
                        <div className="chat-filtro-etiqueta-wrapper">
                          <button
                            type="button"
                            className="chat-filtro-etiqueta-button"
                            onClick={() => setModalFiltroEtiquetasAberto(true)}
                            title={
                              (filtroEtiquetaIds?.length || 0) > 0
                                ? `Filtro ativo: ${filtroEtiquetaIds.length} etiqueta(s)`
                                : "Filtrar por etiquetas"
                            }
                            aria-label="Filtrar por etiquetas"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="23"
                              height="23"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#005848"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M20 12V7a2 2 0 0 0-2-2h-5L4 14l6 6 9-9z" />
                              <circle cx="16.5" cy="7.5" r="1.5" />
                            </svg>
                          </button>

                          {(filtroEtiquetaIds?.length || 0) > 0 && (
                            <span className="chat-filtro-etiqueta-badge" aria-hidden="true">
                              {filtroEtiquetaIds.length > 99 ? "99+" : filtroEtiquetaIds.length}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn-novo-chat"
                          title="Nova conversa"
                          onClick={abrirNovoChat}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ marginRight: 6 }}
                          >
                            <path
                              d="M5 5H19V14C19 14.5523 18.5523 15 18 15H8L5 18V5Z"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 8V12"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                            <path
                              d="M10 10H14"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span className="btn-novo-chat-text">Novo</span>
                        </button>
                      </div>
                      <div className="lista-conversas-atalhos">
                        <button
                          type="button"
                          className={`atalho-chip ${quickFilters.has(QUICK.NAO_LIDAS) ? "ativo" : ""}`}
                          onClick={() => toggleQuick(QUICK.NAO_LIDAS)}
                        >
                          Não lidas
                        </button>

                        <button
                          type="button"
                          className={`atalho-chip ${quickFilters.has(QUICK.NAO_RESPONDIDAS) ? "ativo" : ""}`}
                          onClick={() => toggleQuick(QUICK.NAO_RESPONDIDAS)}
                        >
                          Não respondidas
                        </button>

                        <button
                          type="button"
                          className={`atalho-chip ${quickFilters.has(QUICK.MINHAS) ? "ativo" : ""}`}
                          onClick={() => toggleQuick(QUICK.MINHAS)}
                        >
                          Minhas conversas
                        </button>
                        <button
                          type="button"
                          className={`atalho-chip ${quickFilters.has(QUICK.SEM_INTERACAO) ? "ativo" : ""}`}
                          onClick={() => toggleQuick(QUICK.SEM_INTERACAO)}
                          title="Mostra conversas onde nenhum operador respondeu ainda"
                        >
                          Sem interação
                        </button>
                        {quickFilters.size > 0 && (
                          <button
                            type="button"
                            className="atalho-limpar"
                            onClick={limparQuick}
                          >
                            Limpar
                          </button>
                        )}
                      </div>
                      <div className="lista-conversas-totalizadores">
                        {conversasFiltradas.length > 0 && <>
                          <span>Total</span>
                          <span
                            className="lista-conversas-badge-total"
                            title={`Total de conversas: ${conversasFiltradas.length}`}
                          >
                            {conversasFiltradas.length}
                          </span>
                        </>}


                        {totalConversasNaoLidas > 0 && (
                          <>
                            <span className="lista-conversas-label-nao-lidas">
                              Não lidas
                            </span>
                            <span
                              className="lista-conversas-badge-nao-lidas"
                              title={`Conversas não lidas: ${totalConversasNaoLidas}`}
                            >
                              {totalConversasNaoLidas}
                            </span>
                          </>

                        )}

                        {totalConversasSemInteracao > 0 && (
                          <>
                            <span className="lista-conversas-label-sem-interacao">
                              Sem interação
                            </span>
                            <span
                              className="lista-conversas-badge-sem-interacao"
                              title={`Conversas sem interação: ${totalConversasSemInteracao}`}
                            >
                              {totalConversasSemInteracao}
                            </span>
                          </>
                        )}

                      </div>
                    </div>

                    {loading && conversas.length === 0 && (
                      <div className="lista-conversas-info">
                        Carregando conversas...
                      </div>
                    )}

                    {erro && <div className="lista-conversas-erro">{erro}</div>}

                    {!loading && conversasFiltradas.length === 0 && !erro && (
                      <div className="lista-conversas-info">
                        {filtro
                          ? "Nenhuma conversa encontrada para o filtro."
                          : "Nenhuma conversa encontrada."}
                      </div>
                    )}

                    <div className="lista-conversas-lista">
                      {conversasFiltradas.map(renderConversaItem)}
                    </div>
                  </div>

                  <div className="lista-conversas-chat-area lista-conversas-chat-area--col">
                    <WhatsAppConversationActions
                      conversaSelecionada={conversaSelecionada}
                      actionAtivaCallback={actionAtivaCallback}
                    />

                    <div className="lista-conversas-chat">
                      {conversaSelecionada ? (
                        <WhatsAppChat
                          multchat={true}
                          DesabilitarRecusosAutomaticos={actionAtivada}
                          operadorId={operadorContextoId}
                          NomeContato={conversaSelecionada.nome}
                          DdiCelular={conversaSelecionada.ddi}
                          DddCelular={conversaSelecionada.ddd}
                          NumCelular={conversaSelecionada.telefone}
                        />
                      ) : (
                        <div className="lista-conversas-placeholder whatsapp-style">
                          <h4>Selecione uma conversa</h4>
                          <p>
                            Escolha um contato ao lado para começar o
                            atendimento.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ChatNovoChatSender
              aberto={novoChatAberto}
              onFechar={fecharNovoChat}
              onIniciar={iniciarConversaManual}
            />
            <ChatEtiquetaManager
              aberto={modalFiltroEtiquetasAberto}
              onFechar={() => setModalFiltroEtiquetasAberto(false)}
              usuarioId={operadorContextoId}
              modoConsultaFiltro={true}
              selecionadasInicial={filtroEtiquetaIds}
              onAplicarFiltro={(ids) => {
                setFiltroEtiquetaIds(Array.isArray(ids) ? [...ids] : []);
                setModalFiltroEtiquetasAberto(false);
              }}
            />
            <OperadorContextManager
              aberto={modalOperadorAberto}
              onFechar={() => setModalOperadorAberto(false)}
              session={session}
              selecionadoId={operadorContexto.usuarioId}
              onAplicar={(usuario) => {
                setOperadorContexto(usuario);
                setConversaSelecionada(null);
                setModalOperadorAberto(false);
              }}
              conversas={conversasBaseFiltradas}
            />


          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListaConversasWhatsApp;
